import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BsFillTrashFill } from "react-icons/bs";
import { Stack, Button, Link, Container, Popover, Box, Typography } from "@mui/material";
import { TextField } from "@mui/material";
import {
    fetchAccessLogsAction,
    deleteAccessLogAction,
    exportAccessLogsAction,
} from "../../redux/Actions";
import ResponsiveDialog from "../Modal";
import { isEmpty } from "../../services/validation";
import { strings } from "../../utils/constants";
import AdminPagination from "../AdminPagination";
import NoRecordFound from "../NoRecordFound";

export const AccessLogsTable = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchText, setSearchText] = useState("");
    const [accessType, setAccessType] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [appliedStartDate, setAppliedStartDate] = useState("");
    const [appliedEndDate, setAppliedEndDate] = useState("");
    const [sortBy, setSortBy] = useState("access_datetime");
    const [order, setOrder] = useState("DESC");

    const [subscriptionType, setSubscriptionType] = useState("");
    const dispatch = useDispatch();

    const [open, setOpen] = useState(false);
    const [isDelete, setIsDelete] = useState(false);
    const [logId, setLogId] = useState("");
    const [dateRangeOpen, setDateRangeOpen] = useState(false);
    const [dateRangeAnchor, setDateRangeAnchor] = useState(null);

    const { deleteAccessLog, accessLogs } = useSelector(
        (state) => state.adminAccessLogsReducer
    );

    const tableRecords = accessLogs?.result ? accessLogs.result : (accessLogs?.data ? accessLogs.data : []);

    const totalPage = Math.ceil((accessLogs?.total || accessLogs?.data?.total || 0) / 10);



    const getAPI = async (page, searchText, accessType) => {
        let requestInfo = {
            page: page,
            limit: 10,
            searchText: searchText,
            access_type: accessType,
            start_date: startDate,
            end_date: endDate,
            subscription_type: subscriptionType,
            sortBy: sortBy,
            order: order,
            language: strings.language.EN,
        };
        await dispatch(fetchAccessLogsAction(requestInfo));
    };

    useEffect(() => {
        getAPI(1, "", "");
    }, []);

    useEffect(() => {
        if (isDelete) {
            if (deleteAccessLog?.success) {
                getAPI(currentPage, searchText, accessType);
            }
            setIsDelete(false);
            setOpen(false);
        }
    }, [deleteAccessLog]);

    const nextPage = () => {
        if (currentPage !== totalPage) {
            setCurrentPage(currentPage + 1);
            getAPI(currentPage + 1, searchText, accessType);
        }
    };

    const prevPage = () => {
        if (currentPage !== 1) {
            setCurrentPage(currentPage - 1);
            getAPI(currentPage - 1, searchText, accessType);
        }
    };

    const changePage = (id) => {
        setCurrentPage(id);
        getAPI(id, searchText, accessType);
    };

    const searchHandler = () => {
        setAccessType("");
        if (isEmpty(searchText)) {
            setSearchText("");
            setCurrentPage(1);
            getAPI(1, "", "");
        } else {
            setCurrentPage(1);
            getAPI(1, searchText, "");
        }
    };

    const combinedFilterClickHandler = (accessTypeFilter, subscriptionTypeFilter) => {
        setAccessType(accessTypeFilter);
        setSubscriptionType(subscriptionTypeFilter);
        setCurrentPage(1);
        let requestInfo = {
            page: 1,
            limit: 10,
            searchText: searchText,
            access_type: accessTypeFilter,
            start_date: appliedStartDate,
            end_date: appliedEndDate,
            subscription_type: subscriptionTypeFilter,
            language: strings.language.EN,
        };
        dispatch(fetchAccessLogsAction(requestInfo));
    };

    const deleteHandler = (data) => {
        setLogId(data.id);
        setOpen(true);
    };

    const negativeClickHandler = () => {
        setIsDelete(false);
        setOpen(false);
    };

    const positiveClickHandler = async () => {
        setIsDelete(true);
        const response = await dispatch(
            deleteAccessLogAction({
                language: strings.language.EN,
                logId: logId,
            })
        );
        if (response?.payload?.success) {
            getAPI(currentPage, searchText, accessType);
        }
    };

    const closeHandler = () => {
        setOpen(false);
    };

    const formatDateForDisplay = (dateStr) => {
        if (!dateStr) return "dd/mm/yyyy";
        const date = new Date(dateStr);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const handleDateRangeClick = (event) => {
        setDateRangeAnchor(event.currentTarget);
        setDateRangeOpen(true);
    };

    const handleDateRangeClose = () => {
        setDateRangeOpen(false);
        setDateRangeAnchor(null);
    };

    const handleDateRangeApply = () => {
        setAppliedStartDate(startDate);
        setAppliedEndDate(endDate);
        setCurrentPage(1);
        getAPI(1, searchText, accessType);
        handleDateRangeClose();
    };

    const handleSort = (column) => {
        const newOrder = sortBy === column && order === "ASC" ? "DESC" : "ASC";
        setSortBy(column);
        setOrder(newOrder);

        let requestInfo = {
            page: currentPage,
            limit: 10,
            searchText: searchText,
            access_type: accessType,
            start_date: appliedStartDate,
            end_date: appliedEndDate,
            subscription_type: subscriptionType,
            sortBy: column,
            order: newOrder,
            language: strings.language.EN,
        };
        dispatch(fetchAccessLogsAction(requestInfo));
    };

    const handleExport = async () => {
        let requestInfo = {
            searchText: searchText,
            access_type: accessType,
            start_date: appliedStartDate,
            end_date: appliedEndDate,
            subscription_type: subscriptionType,
            sortBy: sortBy,
            order: order,
            language: strings.language.EN,
        };

        const result = await dispatch(exportAccessLogsAction(requestInfo));

        if (result.payload) {
            const link = document.createElement('a');
            link.download = `access-logs-${new Date().toISOString().split('T')[0]}.csv`;

            const blob = new Blob([result.payload], { type: "text/csv;charset=utf-8;" });
            const url = window.URL.createObjectURL(blob);
            link.href = url;
            link.click();
            window.URL.revokeObjectURL(url);
        }
    };

    return (
        <>
            <Container className="container-wrapper">
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                    <Button
                        variant="contained"
                        size="small"
                        disabled={tableRecords.length === 0}
                        sx={{
                            color: "#184437",
                        }}
                        className="ubutton"
                        onClick={handleExport}
                    >
                        {strings.words.EXPORT}
                    </Button>
                </div>
                <Stack
                    direction={{ xm: "column", lg: "row" }}
                    alignItems="end"
                    justifyContent="right"
                    mb={4}
                >
                    <Button
                        variant="outlined"
                        onClick={handleDateRangeClick}
                        sx={{
                            fontWeight: appliedStartDate && appliedEndDate ? "bold" : "normal",
                            height: "40px",
                            minWidth: "200px",
                            border: "1px solid #184437",
                            color: "#184437",
                        }}
                    >
                        {appliedStartDate && appliedEndDate ? `${formatDateForDisplay(appliedStartDate)} - ${formatDateForDisplay(appliedEndDate)}` : "DD/MM/YYYY - DD/MM/YYYY"}
                    </Button>

                    <div className="uinput">
                        <TextField
                            id="search"
                            variant="outlined"
                            size="small"
                            placeholder={strings.words.SEARCH}
                            value={searchText}
                            sx={{ color: "#184437" }}
                            onChange={(e) => setSearchText(e.target.value)}
                            autoComplete="off"
                        />
                    </div>
                    <Button
                        variant="contained"
                        sx={{ color: "#184437" }}
                        className="ubutton"
                        onClick={searchHandler}
                    >
                        {strings.words.SEARCH}
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ color: "#184437" }}
                        className="ubutton"
                        onClick={() => {
                            setSearchText("");
                            setStartDate("");
                            setEndDate("");
                            setAppliedStartDate("");
                            setAppliedEndDate("");
                            setSubscriptionType("");
                            setAccessType("");
                            setSortBy("access_datetime");
                            setOrder("DESC");
                            setCurrentPage(1);

                            let requestInfo = {
                                page: 1,
                                limit: 10,
                                searchText: "",
                                access_type: "",
                                start_date: "",
                                end_date: "",
                                subscription_type: "",
                                sortBy: "access_datetime",
                                order: "DESC",
                                language: strings.language.EN,
                            };
                            dispatch(fetchAccessLogsAction(requestInfo));
                        }}
                    >
                        {strings.words.RESET}
                    </Button>
                    <div className="align-self-xl-center mt-3 text-end filter-dropdow-main filterIcon-wrapper">
                        <Link
                            className="px-0 filter-dropdown"
                            id="filter"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                        >
                            <img
                                src="/assets/icons/filter-icon.svg"
                                width="34"
                                height="30"
                                alt="filter icon"
                            />
                        </Link>
                        <div
                            className="dropdown-menu dropdown-menu-end"
                            aria-labelledby="filter"
                        >
                            <Link
                                className="dropdown-item"
                                onClick={combinedFilterClickHandler.bind(this, "web", "")}
                            >
                                {strings.words.WEB_ACCESS}
                            </Link>
                            <Link
                                className="dropdown-item"
                                onClick={combinedFilterClickHandler.bind(this, "desktop", "")}
                            >
                                {strings.words.DESKTOP_ACCESS}
                            </Link>
                            <Link
                                className="dropdown-item"
                                onClick={combinedFilterClickHandler.bind(this, "", "trial")}
                            >
                                {strings.words.TRIAL_SUBSCRIPTIONS}
                            </Link>
                            <Link
                                className="dropdown-item"
                                onClick={combinedFilterClickHandler.bind(this, "", "active")}
                            >
                                {strings.words.ACTIVE_SUBSCRIPTIONS}
                            </Link>
                            <Link
                                className="dropdown-item"
                                onClick={combinedFilterClickHandler.bind(this, "", "expired")}
                            >
                                {strings.words.EXPIRED_SUBSCRIPTIONS}
                            </Link>
                        </div>
                    </div>
                </Stack>
            </Container>



            <div className="ftable-wrapper">
                <table className="ftable">
                    <thead>
                        <tr>
                            <th>{strings.general.SR_NO}</th>
                            <th className="w-25" style={{ cursor: 'pointer' }} onClick={() => handleSort('access_datetime')}>
                                {strings.words.ACCESS_DATE} {sortBy === 'access_datetime' && (order === 'ASC' ? '↑' : '↓')}
                            </th>
                            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('access_type')}>
                                {strings.words.ACCESS_TYPE} {sortBy === 'access_type' && (order === 'ASC' ? '↑' : '↓')}
                            </th>
                            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('language_preference')}>
                                {strings.words.LANGUAGE} {sortBy === 'language_preference' && (order === 'ASC' ? '↑' : '↓')}
                            </th>
                            <th className="w-25" style={{ cursor: 'pointer' }} onClick={() => handleSort('source')}>
                                {strings.words.SOURCE} {sortBy === 'source' && (order === 'ASC' ? '↑' : '↓')}
                            </th>
                            <th className="w-25" style={{ cursor: 'pointer' }} onClick={() => handleSort('user_email')}>
                                {strings.words.USER_EMAIL} {sortBy === 'user_email' && (order === 'ASC' ? '↑' : '↓')}
                            </th>
                            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('subscription_type')}>
                                {strings.words.SUBSCRIPTION} {sortBy === 'subscription_type' && (order === 'ASC' ? '↑' : '↓')}
                            </th>
                            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('app_version')}>
                                {strings.words.APP_VERSION} {sortBy === 'app_version' && (order === 'ASC' ? '↑' : '↓')}
                            </th>
                            <th className="w-25" style={{ cursor: 'pointer' }} onClick={() => handleSort('additional_info')}>
                                {strings.words.ADDITIONAL_INFO} {sortBy === 'additional_info' && (order === 'ASC' ? '↑' : '↓')}
                            </th>
                            <th>{strings.words.ACTIONS}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tableRecords.length > 0 ? (
                            tableRecords.map((row, idx) => {
                                return (
                                    <tr key={idx}>
                                        <td className="table-cell">{(currentPage - 1) * 10 + idx + 1}</td>
                                        <td className="table-cell w-25">{(() => {
                                            const date = new Date(row.access_datetime);
                                            const day = date.getDate().toString().padStart(2, '0');
                                            const month = (date.getMonth() + 1).toString().padStart(2, '0');
                                            const year = date.getFullYear();
                                            const hours = date.getHours().toString().padStart(2, '0');
                                            const minutes = date.getMinutes().toString().padStart(2, '0');
                                            return `${day}/${month}/${year} ${hours}:${minutes}`;
                                        })()}</td>
                                        <td className="table-cell">{row.access_type}</td>
                                        <td className="table-cell">{row.language_preference}</td>
                                        <td className="table-cell w-25">{row.source || ''}</td>
                                        <td className="table-cell w-25">{row.user_email || ''}</td>
                                        <td className="table-cell">{row.subscription_type || ''}</td>
                                        <td className="table-cell">{row.app_version || ''}</td>
                                        <td className="table-cell w-25">{row.additional_info || ''}</td>
                                        <td className="fit">
                                            <span className="factions mt-1">
                                                <BsFillTrashFill
                                                    className="fdelete-btn cursor-pointer"
                                                    onClick={deleteHandler.bind(this, row)}
                                                />
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <NoRecordFound colSpan={10} />
                        )}
                    </tbody>
                </table>
                {tableRecords.length > 0 && (
                    <AdminPagination
                        currentPage={currentPage}
                        totalRecords={accessLogs?.total || accessLogs?.data?.total || 0}
                        nextPage={nextPage}
                        prevPage={prevPage}
                        changePage={changePage}
                    />
                )}
            </div >

            <Popover
                open={dateRangeOpen}
                anchorEl={dateRangeAnchor}
                onClose={handleDateRangeClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Box sx={{ p: 3, minWidth: 300 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: "#184437" }}>
                        Select Date Range
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1, color: "#666" }}>
                            Start Date
                        </Typography>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            style={{
                                width: "100%",
                                padding: "10px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                fontSize: "14px"
                            }}
                        />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ mb: 1, color: "#666" }}>
                            End Date
                        </Typography>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            style={{
                                width: "100%",
                                padding: "10px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                fontSize: "14px"
                            }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                            variant="text"
                            onClick={handleDateRangeClose}
                            sx={{ color: "#666" }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleDateRangeApply}
                            disabled={!startDate || !endDate}
                            sx={{
                                backgroundColor: "#184437",
                                '&:hover': {
                                    backgroundColor: "#0f2e26"
                                },
                                '&:disabled': {
                                    backgroundColor: "#ccc",
                                    color: "#666"
                                }
                            }}
                        >
                            Apply
                        </Button>
                    </Box>
                </Box>
            </Popover>

            <ResponsiveDialog
                open={open}
                negativeButtonClickHandler={negativeClickHandler}
                positiveButtonClickHandler={positiveClickHandler}
                handleClose={closeHandler}
                titleText={strings.general.ARE_YOU_SURE}
                subTitleText={strings.general.DELETE_ACCESS_LOG_CONFIRM}
                negativeButtonText={strings.words.CANCEL}
                positiveButtonText={strings.words.YES}
            />
        </>
    );
};